import _ from "lodash";
import type { RefObject } from "react";
import React from "react";
import type { IDurationData } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import {
    ALL_CHANNELS,
    BRANDED_NON_BRANDED,
    BRANDED_NON_BRANDED_CATEGORY,
    NUMBERS,
    ORGANIC_PAID,
    ORGANIC_PAID_CATEGORY,
    PERCENTS,
    searchTrafficGraphTabsConfig,
    SEARCH_TYPE,
    SEARCH_TYPE_CATEGORY,
    timeGranularityList,
    MMX_ORGANIC_PAID,
    MMX_ORGANIC_PAID_CATEGORY,
} from "./Helpers/SearchOverviewGraphConfig";
import {
    CHANGE_CATEGORY,
    CHANGE_DATA_TYPE,
    CHANGE_METRIC,
    ERROR,
    IS_LOADING,
    NEW_DATA,
    RESET_CHANNEL_AND_CATEGORY,
    SELECT_CHANNEL,
    SET_IS_MONTH_TO_DATE_ACTIVE,
    SET_TIME_GRANULARITY,
    TOGGLE_LEGEND_ITEM,
} from "./SearchOverviewGraphActions";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

type ITabType = (typeof searchTrafficGraphTabsConfig)[0] & { value?: number };
type IGranularityType = typeof timeGranularityList.daily;
type IDataType = "NUMBERS" | "PERCENTS";

export interface ICategory {
    id: string;
    title: string;
}

interface ILegendItemState {
    rawName: string;
    visible: boolean;
}

enum Metrics {
    TrafficShare = "TrafficShare",
    AverageDuration = "AverageDuration",
    PagesPerVisit = "PagesPerVisit",
    BounceRate = "BounceRate",
}

type IMetricDataType = {
    [key in Metrics]: {
        BRANDED_NON_BRANDED?: IDataType | null;
        ORGANIC_PAID?: IDataType | null;
        SEARCH_TYPE?: IDataType | null;
    };
};

export interface ISearchOverviewState {
    selectedMetricTab: ITabType;
    category: ICategory;
    channel: string;
    dataType: IMetricDataType;
    durationObj: IDurationData;
    duration: string;
    country: string;
    webSource: string;
    breakdownFilter?: string;
    granularity?: IGranularityType;
    includeSubDomains: boolean;
    keys: string | string[];
    rawData: any;
    isLoading: boolean;
    isError: boolean;
    from: string;
    to: string;
    isWindow: boolean;
    chartRef: RefObject<HTMLDivElement | null>;
    isSingle: boolean;
    isMobileWeb: boolean;
    isDesktop: boolean;
    isTotal: boolean;
    legendItemsState: ILegendItemState[];
    totals: any;
    isMonthsToDateActive: boolean;
}

const defaultDataType = {
    TrafficShare: {
        BRANDED_NON_BRANDED: "PERCENTS",
        ORGANIC_PAID: "NUMBERS",
        SEARCH_TYPE: "PERCENTS",
        MMX_ORGANIC_PAID: "NUMBERS",
    },
    AverageDuration: {
        ORGANIC_PAID: "NUMBERS",
    },
    PagesPerVisit: {
        ORGANIC_PAID: "NUMBERS",
    },
    BounceRate: {
        ORGANIC_PAID: "PERCENTS",
    },
};

const LAST_28_DAYS_AS_QUERY_PARAM_VALUE = "28d";

const gertDefaultGranularity = (duration) =>
    DurationService.isGreaterThanThreeMonths(duration)
        ? timeGranularityList.monthly.name
        : timeGranularityList.weekly.name;

export const initState = ({
    key,
    country,
    webSource,
    duration,
    isWWW,
    selectedMetricTab,
    category,
    dataType,
    breakdownFilter = null,
    granularity,
    rawData = null,
    isLoading = true,
    isError = false,
    isSingle,
    isMobileWeb,
    isTotal,
    isDesktop,
    totals = null,
    channel = null,
}): ISearchOverviewState => {
    const durationObj = DurationService.getDurationData(duration);
    const { from, to, isWindow } = durationObj.forAPI;
    const defaultCategory = isDesktop ? ORGANIC_PAID_CATEGORY : MMX_ORGANIC_PAID_CATEGORY;

    return validateAndApplyChanges({
        selectedMetricTab: selectedMetricTab ?? searchTrafficGraphTabsConfig[0],
        category: category ?? defaultCategory,
        dataType: dataType ?? defaultDataType,
        channel: channel ?? ALL_CHANNELS,
        durationObj,
        duration,
        country,
        webSource,
        keys: key,
        breakdownFilter,
        isError,
        granularity: granularity ?? gertDefaultGranularity(duration),
        includeSubDomains: isWWW === "*",
        rawData,
        isLoading,
        isWindow,
        from,
        to,
        isSingle,
        isMobileWeb,
        isTotal,
        isDesktop,
        legendItemsState: [],
        chartRef: React.createRef<HTMLDivElement>(),
        totals,
        isMonthsToDateActive: duration === LAST_28_DAYS_AS_QUERY_PARAM_VALUE,
    });
};

export const isCategoryAvailableForState = _.curry((state, category) => {
    switch (category.id) {
        case ORGANIC_PAID:
            return state.isDesktop;
        case BRANDED_NON_BRANDED:
            return state.selectedMetricTab.name === "TrafficShare" && !state.isWindow;
        case SEARCH_TYPE:
            return state.isDesktop && state.selectedMetricTab.name === "TrafficShare" && !state.isWindow;
        case MMX_ORGANIC_PAID:
            return !state.isDesktop;
    }
});

export const isDataTypeAvailableForState = _.curry((state, dataType) => {
    switch (state.selectedMetricTab.name) {
        case Metrics.TrafficShare:
            if (state.category.id === BRANDED_NON_BRANDED_CATEGORY.id || state.category.id === SEARCH_TYPE_CATEGORY.id) {
                return dataType === PERCENTS;
            }
            return true;
        case Metrics.BounceRate:
            return dataType === PERCENTS;
        default:
            return dataType === NUMBERS;
    }
});

function validateAndApplyChanges(nextState) {
    if (!isCategoryAvailableForState(nextState, nextState.category)) {
        nextState = {
            ...nextState,
            legendItemsState: [],
            category: nextState.webSource === devicesTypes.DESKTOP ? ORGANIC_PAID_CATEGORY : MMX_ORGANIC_PAID_CATEGORY,
            channel: ALL_CHANNELS,
        };
    }

    return nextState;
}

function toggleLegendItem(legendItemsState, legendItem) {
    const { rawName, visible } = legendItem;
    const itemInState = legendItemsState.find(({ rawName: n }) => n === rawName);
    if (itemInState) {
        return legendItemsState.map((item) => {
            if (item === itemInState) {
                return {
                    ...item,
                    visible: !visible,
                };
            }
            return item;
        });
    }
    return [...legendItemsState, { rawName, visible: !visible }];
}

function recurseAllChannels(obj) {
    return Object.entries(obj).reduce((previous, [key, value]) => {
        if (_.isPlainObject(value)) {
            previous[key] = recurseAllChannels(value);
        } else {
            previous[key] = value;
        }
        return previous;
    }, {} as any);
}

function processDataForNextState(nextState) {
    if (nextState.isMobileWeb && nextState.isSingle && nextState.rawData) {
        return {
            ...nextState,
            rawData: recurseAllChannels(nextState.rawData),
        };
    }
    return nextState;
}

export function searchOverviewGraphReducer(state: ISearchOverviewState, action): ISearchOverviewState {
    switch (action.type) {
        case NEW_DATA:
            return processDataForNextState({
                ...state,
                isError: false,
                rawData: action.rawData,
                totals: action.totals,
            });
        case IS_LOADING:
            return {
                ...state,
                isError: false,
                isLoading: action.isLoading,
            };
        case ERROR:
            return { ...state, isError: true };
        case CHANGE_METRIC:
            return validateAndApplyChanges({
                ...state,
                selectedMetricTab: action.selectedMetricTab,
            });
        case CHANGE_CATEGORY:
            return validateAndApplyChanges({
                ...state,
                category: action.index,
                legendItemsState: [],
            });
        case CHANGE_DATA_TYPE:
            return validateAndApplyChanges({
                ...state,
                dataType: action.dataType,
            });
        case SET_TIME_GRANULARITY:
            return {
                ...state,
                granularity: action.timeGranularity,
            };
        case SET_IS_MONTH_TO_DATE_ACTIVE:
            return {
                ...state,
                isMonthsToDateActive: action.isMonthsToDateActive,
            };
        case TOGGLE_LEGEND_ITEM:
            return {
                ...state,
                legendItemsState: toggleLegendItem(state.legendItemsState, action.item),
            };
        case SELECT_CHANNEL:
            return validateAndApplyChanges({
                ...state,
                channel: action.channel,
                category: action.category,
            });
        case RESET_CHANNEL_AND_CATEGORY:
            return validateAndApplyChanges({
                ...state,
                channel: ALL_CHANNELS,
                category: state.webSource === devicesTypes.DESKTOP ? ORGANIC_PAID_CATEGORY : MMX_ORGANIC_PAID_CATEGORY,
                dataType: defaultDataType,
            });
        default:
            return state;
    }
}
